import { useAuth } from "@healthos/auth/react";
import { useEffect, useRef } from "react";
import { client, jsonOrThrow } from "../api/healthos";
import {
  createTeamsSsoTokenRequester,
  getActiveHostAuth,
  isTeamsParentOrigin,
} from "../lib/host-auth";

/**
 * Links the signed-in Teams tab session's Microsoft Entra identity to this
 * HealthOS account, so the Teams bot, message extension and Activity Feed
 * have a `chat_identities` row for this member. Mounted once for the whole
 * app, so it runs whichever page the tab opens on (the bot's Sign in card
 * opens Home). Fires at most once per signed-in session, independent of the
 * "How we reach you" Teams toggle - linking and the delivery preference are
 * separate concerns.
 *
 * The identity is proven by the Teams host's SSO token, which the server
 * verifies; this hook never sends ids of its own. Any failure (an older
 * wrapper that doesn't answer the request, consent not granted, an identity
 * already linked to another member) is a silent no-op: the member keeps
 * using the app, and the next tab load tries again. It is deliberately not
 * retried on every auth-state change (each token refresh), so a failing
 * link never turns into a loop.
 */
export function useTeamsTabLink(): void {
  const { state } = useAuth();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (state.status !== "signedIn") return;
    const ctx = getActiveHostAuth();
    if (!ctx || !isTeamsParentOrigin(ctx.parentOrigin)) return;

    attempted.current = true;
    void (async () => {
      try {
        const teamsToken = await createTeamsSsoTokenRequester(ctx)();
        await jsonOrThrow(
          await client().api.chat["teams-link"].$post({ json: { teamsToken } }),
          "POST /api/chat/teams-link",
        );
      } catch {
        // Best-effort: never block the app on this.
      }
    })();
  }, [state]);
}
